/**
 * @file 请求错误文案解析
 * @module utils/errorMessage
 * @description 将业务错误、axios 响应错误和普通 Error 转换为用户可读提示。
 */
import axios, { type AxiosError } from 'axios'
import type { ApiResponse } from '@/types/apiResponse'

/** 业务响应体 */
type ApiErrorBody = ApiResponse

/** axios 错误响应体。 */
type ApiErrorData = ApiErrorBody | string

/**
 * 判断值是否为带 msg 的业务错误对象。
 * @param value 已确认可按对象处理的错误响应值。
 * @returns 存在非空字符串 msg 字段时返回 true。
 */
const isApiErrorBody = (value: object | null): value is ApiErrorBody => {
  return value !== null && 'msg' in value && typeof value.msg === 'string'
}

/**
 * 将 HTTP 状态码转换为默认错误文案。
 * @param status axios 响应状态码。
 * @returns 状态码对应的用户提示；未命中时返回 null。
 */
const getHttpStatusMessage = (status?: number): string | null => {
  if (status === 403) return '没有权限访问'
  if (status === 401) return '请先登录'
  if (status === 404) return '资源不存在'
  if (status === 422) return '提交数据格式不正确，请刷新页面后重试'
  if (status && status >= 500) return '服务器异常，请稍后重试'
  return null
}

/**
 * 从 axios / 业务 reject 中提取可读错误文案。
 * @param error catch 或 Promise reject 捕获到的任意错误值。
 * @returns 适合 toast/message 展示的错误文案。
 */
export const extractAxiosErrorMessage = <T>(error: T): string => {
  if (typeof error === 'object' && isApiErrorBody(error)) {
    const msg = error.msg?.trim()
    if (msg) return msg
  }

  if (axios.isAxiosError<ApiErrorData>(error)) {
    const axiosErr: AxiosError<ApiErrorData> = error
    const data = axiosErr.response?.data
    if (typeof data === 'string' && data.trim()) {
      return data.trim()
    }
    if (typeof data === 'object' && isApiErrorBody(data)) {
      const msg = data.msg?.trim()
      if (msg) return msg
    }
    const statusMessage = getHttpStatusMessage(axiosErr.response?.status)
    if (statusMessage) return statusMessage
  }

  if (error instanceof Error) {
    const msg = error.message.trim()
    if (msg && !/^Request failed with status code \d+$/.test(msg)) {
      return msg
    }
  }
  return '未知错误'
}
